import { IconButton, InputAdornment, Typography } from '@material-ui/core';
import { useFormikContext } from 'formik';
import React, { useEffect } from 'react';
import QrCodeIcon from '../../../../../core/src/media/icons/QrCodeIcon';
import { getDistanceUnitLabel } from '../../../../../core/src/util';
import { Checkbox } from '../../util/form2/Checkbox';
import { SelectHubType } from '../../util/form2/SelectHubType';
import { TextField } from '../../util/form2/TextField';

/** Helper text explaining where to measure the hub offset distance */
export const HUB_OFFSET_HELPER_TEXT = (
  <>
    Input distance between the <u> Bottom of the Headspace Sensor </u>and the
    <u> Top of the Top Ring </u>of the Bin :
  </>
);

/**
 * A form component for assigning and configuring hub settings in a grain container system.
 * Handles input fields for hub identification, type selection, and sensor configurations.
 * Provides additional fields for dual-point sensor measurements when enabled.
 * Uses Formik context for form state management.
 */
export const AssignHubFormFields: React.FunctionComponent<{
  /** Flag indicating if this is an update operation rather than a new assignment */
  update?: boolean;
  /** Callback function to open QR code scanner dialog for hub ID input */
  openQRScannerDialog?: () => void;
}> = ({ update, openQRScannerDialog }) => {
  const { values, setFieldValue } = useFormikContext<any>();
  const distanceUnitLabel = getDistanceUnitLabel();

  useEffect(() => {
    // reset hub_offset_ft when has_level_sensor is unchecked
    if (!values.has_level_sensor) {
      setFieldValue('hub_offset_ft', 0);
    }
  }, [values.has_level_sensor]);
  return (
    <>
      {!update && (
        <TextField
          name="core_id"
          id="assign-hub"
          label="Hub ID"
          placeholder="AmAg_00-0-000"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  aria-label="open QR code scanner"
                  onClick={() => openQRScannerDialog && openQRScannerDialog()}
                >
                  <QrCodeIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
          fullWidth
          required
        />
      )}
      <TextField
        style={{ marginTop: 10 }}
        name="hub_nickname"
        label="Hub Nickname"
        placeholder="Headspace Hub"
        fullWidth
        required
      />
      <SelectHubType
        style={{ marginTop: 10 }}
        name="hub_type"
        label="Hub Type"
        placeholder="None Selected"
        fullWidth
        required
      />
      <Checkbox
        style={{ marginTop: 10, width: '100%' }}
        name="has_level_sensor"
        label="Level Sensor Add-on Equipped"
      />

      {values.has_level_sensor && (
        <>
          <Typography variant="subtitle2" style={{ marginBottom: 5, marginTop: 5 }}>
            {HUB_OFFSET_HELPER_TEXT}
          </Typography>
          <TextField
            required
            fullWidth
            name="hub_offset_ft"
            label={`Hub Offset (${distanceUnitLabel})`}
            inputProps={{
              inputMode: 'decimal',
            }}
            type="number"
          />
        </>
      )}

      {/* Checkbox to enable dual-point sensor configuration. When checked, displays additional 
          fields for configuring distances and offsets between sensors */}
      {/*<Checkbox*/}
      {/*  style={{ marginTop: 10, width: '100%' }}*/}
      {/*  name="has_pair_level_sensor"*/}
      {/*  label="Has Dual-point Sensor"*/}
      {/*/>*/}
      {/* Conditional rendering block that shows additional configuration fields when dual-point sensor is enabled.
          These fields include various distance measurements and offsets required for proper sensor positioning and calibration,
          such as hub offset, sidewall distances, cable lengths, and axis offsets. All measurements use the same distance unit. */}
      {/*{values.has_pair_level_sensor && (*/}
      {/*  <>*/}
      {/*    <TextField*/}
      {/*      required*/}
      {/*      fullWidth*/}
      {/*      style={{ marginTop: 10 }}*/}
      {/*      name="sideWall_headspace_distance_ft"*/}
      {/*      label={`Sidewall to Headspace Distance (${distanceUnitLabel})`}*/}
      {/*      inputProps={{*/}
      {/*        inputMode: 'decimal',*/}
      {/*      }}*/}
      {/*      type="number"*/}
      {/*    />*/}
      {/*    <TextField*/}
      {/*      required*/}
      {/*      fullWidth*/}
      {/*      style={{ marginTop: 10 }}*/}
      {/*      name="sideWall_auxilary_distance_ft"*/}
      {/*      label={`Sidewall to Auxiliary Distance (${distanceUnitLabel})`}*/}
      {/*      inputProps={{*/}
      {/*        inputMode: 'decimal',*/}
      {/*      }}*/}
      {/*      type="number"*/}
      {/*    />*/}
      {/*    <TextField*/}
      {/*      required*/}
      {/*      fullWidth*/}
      {/*      style={{ marginTop: 10 }}*/}
      {/*      name="headspace_cablesensor_length_ft"*/}
      {/*      label={`Headspace Cable Sensor Length (${distanceUnitLabel})`}*/}
      {/*      inputProps={{*/}
      {/*        inputMode: 'decimal',*/}
      {/*      }}*/}
      {/*      type="number"*/}
      {/*    />*/}
      {/*    <TextField*/}
      {/*      required*/}
      {/*      fullWidth*/}
      {/*      style={{ marginTop: 10 }}*/}
      {/*      name="aux_cablesensor_length_ft"*/}
      {/*      label={`Auxiliary Cable Sensor Length (${distanceUnitLabel})`}*/}
      {/*      inputProps={{*/}
      {/*        inputMode: 'decimal',*/}
      {/*      }}*/}
      {/*      type="number"*/}
      {/*    />*/}
      {/*    <TextField*/}
      {/*      required*/}
      {/*      fullWidth*/}
      {/*      style={{ marginTop: 10 }}*/}
      {/*      name="hub_centerAxis_offset_ft"*/}
      {/*      label={`Hub Center Axis Offset (${distanceUnitLabel})`}*/}
      {/*      inputProps={{*/}
      {/*        inputMode: 'decimal',*/}
      {/*      }}*/}
      {/*      type="number"*/}
      {/*    />*/}
      {/*    <TextField*/}
      {/*      required*/}
      {/*      fullWidth*/}
      {/*      style={{ marginTop: 10 }}*/}
      {/*      name="auxlevel_centerAxis_offset_ft"*/}
      {/*      label={`Auxiliary Level Center Axis Offset (${distanceUnitLabel})`}*/}
      {/*      inputProps={{*/}
      {/*        inputMode: 'decimal',*/}
      {/*      }}*/}
      {/*      type="number"*/}
      {/*    />*/}
      {/*    <TextField*/}
      {/*      required*/}
      {/*      fullWidth*/}
      {/*      style={{ marginTop: 10 }}*/}
      {/*      name="auxlevel_offset_ft"*/}
      {/*      label={`Auxiliary Level Offset (${distanceUnitLabel})`}*/}
      {/*      inputProps={{*/}
      {/*        inputMode: 'decimal',*/}
      {/*      }}*/}
      {/*      type="number"*/}
      {/*    />*/}
      {/*  </>*/}
      {/*)}*/}
    </>
  );
};
